import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import { useAuth } from "../context/AuthContext";
import Tooltip from "../components/common/Tooltip";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { listStaff } from "../api/staffs";
import { listClients } from "../api/clients";
import ClientSearchAutocomplete from "../components/ClientSearchAutocomplete";
import "./ReportsSectionCompact.css";
import {
  exportSalesByEmployeeCsv,
  getSalesByEmployeeReport,
} from "../api/reports";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import { getSalonTimezone } from "../utils/salonTimezone";
import { SALES_BY_EMPLOYEE_PCT_TOOLTIPS } from "../constants/reportTooltips";
import { SALES_BY_EMPLOYEE_FIELD_TOOLTIPS } from "../constants/reportFieldTooltips";
import { formatClientLabel } from "../utils/clientLabel";
import { digitsOnly } from "../utils/phone";
import { countSearchLetters } from "../utils/normalizeSearch";
import { REPORT_HELP } from "../constants/reportHelpContent";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const REVENUE_OPTIONS = [
  { value: "service", label: "Services" },
  { value: "product", label: "Retail" },
  { value: "gift_card", label: "Gift Card" },
];

const PAYMENT_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "gift_card", label: "Gift Card" },
  { value: "other", label: "Other" },
];

const GROUP_OPTIONS = [
  { value: "employee", label: "Employee" },
  { value: "employee_day", label: "Employee + Day" },
  { value: "employee_category", label: "Employee + Category" },
  { value: "employee_item", label: "Employee + Service/Product" },
  { value: "employee_payment", label: "Employee + Payment Type" },
];

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (Number.isNaN(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `${(num * 100).toFixed(1)}%`;
};

export default function SalesByEmployeeReport() {
  const navigate = useNavigate();
  const { currentSalon, user, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.SALES_BY_EMPLOYEE;
  const resolvedSalonId = useMemo(() => {
    const raw = currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }, [currentSalon, user?.salon_id]);
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const today = formatDateISO(new Date(), salonTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -6), salonTimezone);
  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/manager/reports", { replace: true });
  };

  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [revenueTypes, setRevenueTypes] = useState(["service", "product"]);
  const [paymentTypes, setPaymentTypes] = useState([
    "cash",
    "card",
    "gift_card",
    "other",
  ]);
  const [groupBy, setGroupBy] = useState("employee");
  const [includeTips, setIncludeTips] = useState(true);
  const [includeTaxes, setIncludeTaxes] = useState(false);
  const [includeRefunds, setIncludeRefunds] = useState(true);
  const [status, setStatus] = useState("closed");
  const [minAmount, setMinAmount] = useState("");
  const [clientSearchText, setClientSearchText] = useState("");
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const clientSearchRequestRef = useRef(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("net_sales");
  const [sortDir, setSortDir] = useState("desc");
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [meta, setMeta] = useState({ page: 1, page_size: 50, total_rows: 0 });
  const [mixMeta, setMixMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const includeTipsId = useId();
  const includeTaxesId = useId();
  const includeRefundsId = useId();

  const trimmedClientSearch = clientSearchText.trim();
  const clientSearchDigits = digitsOnly(trimmedClientSearch);
  const clientSearchHasLetters = countSearchLetters(trimmedClientSearch) > 0;
  const clientSearchIsName =
    clientSearchHasLetters && trimmedClientSearch.length >= 3;
  const clientSearchIsPhone =
    !clientSearchHasLetters && clientSearchDigits.length >= 4;
  const canAutocompleteClients = clientSearchIsName || clientSearchIsPhone;
  const hasSelectedClient =
    selectedClientId !== null && selectedClientId !== undefined;

  useEffect(() => {
    let isMounted = true;
    const loadStaff = async () => {
      if (!resolvedSalonId) {
        setStaffOptions([]);
        setStaffLoading(false);
        return;
      }
      setStaffLoading(true);
      try {
        const staff = await listStaff({ salon_id: resolvedSalonId });
        if (!isMounted) return;
        setStaffOptions(Array.isArray(staff) ? staff : []);
      } catch (err) {
        if (!isMounted) return;
        setStaffOptions([]);
        setError((prev) => prev || "Unable to load staff for this salon.");
      } finally {
        if (isMounted) setStaffLoading(false);
      }
    };
    loadStaff();
    return () => {
      isMounted = false;
    };
  }, [resolvedSalonId]);

  useEffect(() => {
    if (!resolvedSalonId || !canAutocompleteClients) {
      setClientSearchResults([]);
      setClientSearchLoading(false);
      return;
    }
    const requestId = ++clientSearchRequestRef.current;
    setClientSearchLoading(true);
    const timerId = setTimeout(async () => {
      try {
        const data = await listClients({
          salon_id: resolvedSalonId,
          search: trimmedClientSearch,
          limit: 20,
          require_query: true,
        });
        if (clientSearchRequestRef.current !== requestId) return;
        setClientSearchResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Client autocomplete failed", err);
        if (clientSearchRequestRef.current === requestId) {
          setClientSearchResults([]);
        }
      } finally {
        if (clientSearchRequestRef.current === requestId) {
          setClientSearchLoading(false);
        }
      }
    }, 250);
    return () => clearTimeout(timerId);
  }, [resolvedSalonId, canAutocompleteClients, trimmedClientSearch]);

  const payloadBase = useMemo(
    () => ({
      salon_id: resolvedSalonId,
      start_date: startDate,
      end_date: endDate,
      employee_ids: employeeIds,
      revenue_types: revenueTypes,
      payment_types: paymentTypes,
      group_by: groupBy,
      include_tips: includeTips,
      include_taxes: includeTaxes,
      include_refunds: includeRefunds,
      status,
      min_amount: minAmount ? Number(minAmount) : null,
      client_id: hasSelectedClient ? selectedClientId : null,
      search: hasSelectedClient ? null : trimmedClientSearch || null,
      sort_by: sortBy,
      sort_dir: sortDir,
    }),
    [
      resolvedSalonId,
      startDate,
      endDate,
      employeeIds,
      revenueTypes,
      paymentTypes,
      groupBy,
      includeTips,
      includeTaxes,
      includeRefunds,
      status,
      minAmount,
      hasSelectedClient,
      selectedClientId,
      trimmedClientSearch,
      sortBy,
      sortDir,
    ]
  );

  const fetchReport = useCallback(
    async (pageOverride = null, pageSizeOverride = null) => {
      if (authLoading) return;
      if (!resolvedSalonId) {
        setError(
          "Unable to determine your current salon. Please refresh or re-select your salon."
        );
        return;
      }
      if (!startDate || !endDate) {
        setError("Select a valid start and end date.");
        return;
      }
      setLoading(true);
      setError("");
      const nextPage = pageOverride || page;
      const nextPageSize = pageSizeOverride || pageSize;
      try {
        const data = await getSalesByEmployeeReport({
          ...payloadBase,
          page: nextPage,
          page_size: nextPageSize,
        });
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setKpis(data?.kpis || null);
        setMeta(data?.meta || { page: nextPage, page_size: nextPageSize, total_rows: 0 });
        setMixMeta(data?.mix_meta || null);
        setPage(nextPage);
      } catch (err) {
        setRows([]);
        setKpis(null);
        setMeta({ page: nextPage, page_size: pageSize, total_rows: 0 });
        setMixMeta(null);
        setError(
          err?.response?.data?.detail ||
          "Unable to load sales by employee report."
        );
      } finally {
        setLoading(false);
      }
    },
    [authLoading, resolvedSalonId, startDate, endDate, page, pageSize, payloadBase]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!resolvedSalonId) return;
    fetchReport(1);
  }, [authLoading, resolvedSalonId, fetchReport]);

  const handleToggleValue = (value, values, setter) => {
    if (values.includes(value)) {
      setter(values.filter((entry) => entry !== value));
    } else {
      setter([...values, value]);
    }
  };

  const resetEmployees = () => {
    setEmployeeIds([]);
  };

  const handleClientSearchChange = useCallback(
    (value) => {
      setClientSearchText(value);
      if (hasSelectedClient) {
        setSelectedClientId(null);
      }
      if (!value.trim()) {
        setClientSearchResults([]);
        setClientSearchOpen(false);
      }
    },
    [hasSelectedClient]
  );

  const handleClientSelect = useCallback((client) => {
    if (!client) return;
    setSelectedClientId(client.id);
    setClientSearchText(formatClientLabel(client, true));
    setClientSearchOpen(false);
    setPage(1);
  }, []);

  const columns = useMemo(() => {
    const cols = [
      { key: "employee", label: "Employee", sortKey: "employee_name", sortType: "text" },
    ];
    if (groupBy === "employee_day") {
      cols.push({ key: "date", label: "Date", sortKey: "day", sortType: "date" });
    }
    if (groupBy === "employee_category") {
      cols.push({
        key: "category",
        label: "Category",
        sortKey: "category_name",
        sortType: "text",
      });
    }
    if (groupBy === "employee_item") {
      cols.push({
        key: "item_type",
        label: "Item Type",
        sortKey: "item_type",
        sortType: "text",
      });
      cols.push({
        key: "item_name",
        label: "Item Name",
        sortKey: "item_name",
        sortType: "text",
      });
    }
    if (groupBy === "employee_payment") {
      cols.push({
        key: "payment_type",
        label: "Payment Type",
        sortKey: "payment_type",
        sortType: "text",
      });
    }
    cols.push(
      { key: "ticket_count", label: "Tickets", sortKey: "tickets", sortType: "number" },
      { key: "net_sales", label: "Net Sales", sortKey: "net_sales", sortType: "number" },
      { key: "tips", label: "Tips", sortKey: "tips", sortType: "number" },
      { key: "tax", label: "Tax", sortKey: "tax", sortType: "number" },
      { key: "gross", label: "Gross", sortKey: "gross", sortType: "number" },
      { key: "refunds", label: "Refunds", sortKey: "refunds", sortType: "number" },
      { key: "cash", label: "Cash", sortKey: "cash", sortType: "number" },
      { key: "card", label: "Card", sortKey: "card", sortType: "number" },
      { key: "gift_card", label: "Gift Card", sortKey: "gift_card", sortType: "number" },
      { key: "other", label: "Other", sortKey: "other", sortType: "number" }
    );
    return cols;
  }, [groupBy]);

  useEffect(() => {
    const sortableKeys = new Set(columns.map((col) => col.sortKey).filter(Boolean));
    if (!sortableKeys.has(sortBy)) {
      setSortBy("net_sales");
      setSortDir("desc");
    }
  }, [columns, sortBy]);

  const handleSort = useCallback(
    (col) => {
      if (!col?.sortKey) return;
      if (col.sortKey === sortBy) {
        setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(col.sortKey);
        setSortDir(col.sortType === "number" ? "desc" : "asc");
      }
      setPage(1);
    },
    [sortBy]
  );

  const totalPages = Math.max(
    1,
    Math.ceil((meta?.total_rows || 0) / (meta?.page_size || pageSize))
  );

  const getTooltip = useCallback(
    (key, context = {}) => {
      const fn = SALES_BY_EMPLOYEE_PCT_TOOLTIPS?.[key];
      if (typeof fn !== "function") return "";
      return fn(mixMeta, context) || "";
    },
    [mixMeta]
  );

  const renderFieldLabel = useCallback((label, tooltipKey) => {
    const tooltip = SALES_BY_EMPLOYEE_FIELD_TOOLTIPS[tooltipKey];
    if (!tooltip) return <span className={LABEL_TEXT}>{label}</span>;
    return (
      <span className="inline-flex items-center gap-2">
        <span className={LABEL_TEXT}>{label}</span>
        <InfoTooltip text={tooltip} label={label} />
      </span>
    );
  }, []);

  const renderPercent = useCallback(
    (value, tooltipKey, options = {}) => {
      const formatted = formatPercent(value);
      if (!formatted) return null;
      const tooltipText = getTooltip(tooltipKey, options.context);
      const className =
        options.className ||
        "text-xs text-slate-500 cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2";
      const content = <span className={className}>{formatted}</span>;
      return tooltipText ? (
        <Tooltip
          text={tooltipText}
          placement="top"
          maxWidth={260}
          offset={8}
          size="compact"
        >
          {content}
        </Tooltip>
      ) : (
        content
      );
    },
    [getTooltip]
  );

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Sales by Employee
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="sales-by-employee-overview"
              className={getSectionClassName("sales-by-employee-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_sales_by_employee"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            {error}
          </div>
        )}

        <div
          id="sales-by-employee-filters"
          className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 overflow-visible ${getSectionClassName(
            "sales-by-employee-filters"
          )}`}
        >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1.2fr] gap-4">
            <div>
              {renderFieldLabel("Start Date", "start_date")}
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`${FIELD_CLASS} w-full`}
              />
            </div>
            <div>
              {renderFieldLabel("End Date", "end_date")}
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`${FIELD_CLASS} w-full`}
              />
            </div>
            <div>
              {renderFieldLabel("Group By", "group_by")}
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className={`${FIELD_CLASS} w-full`}
              >
                {GROUP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_1fr] gap-4">
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                {renderFieldLabel("Employees", "employees")}
                <button
                  type="button"
                  className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                  onClick={resetEmployees}
                >
                  Clear
                </button>
              </div>
              <div className="mt-3 max-h-40 overflow-y-auto space-y-2">
                {staffOptions.map((staff) => (
                  <label
                    key={staff.id}
                    className="flex items-center gap-2 text-sm text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={employeeIds.includes(staff.id)}
                      onChange={() =>
                        handleToggleValue(staff.id, employeeIds, setEmployeeIds)
                      }
                    />
                    {staff.display_name}
                  </label>
                ))}
                {staffLoading || authLoading ? (
                  <div className="text-sm text-slate-500">Loading staff...</div>
                ) : resolvedSalonId && !staffOptions.length ? (
                  <div className="text-sm text-slate-500">No staff found.</div>
                ) : null}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {employeeIds.length
                  ? `${employeeIds.length} selected`
                  : "All employees"}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
              {renderFieldLabel("Revenue Types", "revenue_types")}
              <div className="flex flex-wrap gap-3">
                {REVENUE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 text-sm text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={revenueTypes.includes(option.value)}
                      onChange={() =>
                        handleToggleValue(option.value, revenueTypes, setRevenueTypes)
                      }
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
              {renderFieldLabel("Payment Types", "payment_types")}
              <div className="flex flex-wrap gap-3">
                {PAYMENT_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 text-sm text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={paymentTypes.includes(option.value)}
                      onChange={() =>
                        handleToggleValue(option.value, paymentTypes, setPaymentTypes)
                      }
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              {renderFieldLabel("Ticket Status", "ticket_status")}
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={`${FIELD_CLASS} w-full`}
              >
                <option value="closed">Closed Only</option>
                <option value="all">Open + Closed</option>
              </select>
            </div>
            <div>
              {renderFieldLabel("Minimum Amount", "minimum_amount")}
              <input
                type="number"
                min="0"
                step="0.01"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className={`${FIELD_CLASS} w-full`}
              />
            </div>
            <div>
              {renderFieldLabel("Search Client", "search")}
              <ClientSearchAutocomplete
                value={clientSearchText}
                onChange={handleClientSearchChange}
                results={clientSearchResults}
                onSelect={handleClientSelect}
                isOpen={clientSearchOpen}
                onOpenChange={setClientSearchOpen}
                placeholder="Type 3+ letters or 4+ digits..."
                showPhone
                minCharsLetters={3}
                minCharsDigits={4}
                inputClassName={`${FIELD_CLASS} w-full`}
                wrapClassName="manager-schedule-search"
                dropdownClassName="manager-schedule-search-dropdown"
                loading={clientSearchLoading}
                formatLabel={formatClientLabel}
              />
            </div>
            <div
              id="sales-by-employee-export"
              className={`flex flex-wrap items-end gap-3 ${getSectionClassName(
                "sales-by-employee-export"
              )}`}
            >
              <Tooltip
                text={SALES_BY_EMPLOYEE_FIELD_TOOLTIPS.run_report}
                placement="top"
                maxWidth={260}
                offset={8}
                size="compact"
              >
                <button
                  type="button"
                  onClick={() => fetchReport(1)}
                  className={`${BTN_PRIMARY} min-w-[120px]`}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Run Report"}
                </button>
              </Tooltip>
              <Tooltip
                text={SALES_BY_EMPLOYEE_FIELD_TOOLTIPS.export_csv}
                placement="top"
                maxWidth={260}
                offset={8}
                size="compact"
              >
                <button
                  type="button"
                  onClick={() => exportSalesByEmployeeCsv(payloadBase)}
                  className={`${BTN_OUTLINE} min-w-[120px]`}
                >
                  Export CSV
                </button>
              </Tooltip>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <input
                id={includeTipsId}
                type="checkbox"
                checked={includeTips}
                onChange={() => setIncludeTips((prev) => !prev)}
              />
              <label htmlFor={includeTipsId} className="cursor-pointer">
                Include Tips
              </label>
              <InfoTooltip
                text={SALES_BY_EMPLOYEE_FIELD_TOOLTIPS.include_tips}
                label="Include Tips"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <input
                id={includeTaxesId}
                type="checkbox"
                checked={includeTaxes}
                onChange={() => setIncludeTaxes((prev) => !prev)}
              />
              <label htmlFor={includeTaxesId} className="cursor-pointer">
                Include Taxes in totals
              </label>
              <InfoTooltip
                text={SALES_BY_EMPLOYEE_FIELD_TOOLTIPS.include_taxes}
                label="Include Taxes in totals"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <input
                id={includeRefundsId}
                type="checkbox"
                checked={includeRefunds}
                onChange={() => setIncludeRefunds((prev) => !prev)}
              />
              <label htmlFor={includeRefundsId} className="cursor-pointer">
                Include Refunds
              </label>
              <InfoTooltip
                text={SALES_BY_EMPLOYEE_FIELD_TOOLTIPS.include_refunds}
                label="Include Refunds"
              />
            </div>
          </div>
        </div>

        <div
          id="sales-by-employee-kpis"
          className={`grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4 ${getSectionClassName(
            "sales-by-employee-kpis"
          )}`}
        >
          {[
            { label: "Total Net Sales", value: formatMoney(kpis?.total_net_sales) },
            { label: "Total Tips", value: formatMoney(kpis?.total_tips) },
            { label: "Total Tax", value: formatMoney(kpis?.total_tax) },
            { label: "Total Gross", value: formatMoney(kpis?.total_gross) },
            { label: "Total Refunds", value: formatMoney(kpis?.total_refunds) },
            {
              label: "Avg Ticket",
              value: formatMoney(kpis?.avg_ticket_value),
            },
          ].map((tile) => (
            <div
              key={tile.label}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="text-xs uppercase tracking-wide text-slate-500">
                {tile.label}
              </div>
              <div className="mt-2 text-xl font-semibold text-slate-900">
                {tile.value}
              </div>
              {tile.label === "Avg Ticket" && (
                <div className="text-xs text-slate-500 mt-1">
                  Tickets: {kpis?.ticket_count ?? 0}
                </div>
              )}
            </div>
          ))}
        </div>

        <div
          id="sales-by-employee-details"
          className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(
            "sales-by-employee-details"
          )}`}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-3 text-left font-semibold"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(col)}
                        className="inline-flex items-center gap-1 text-left font-semibold text-slate-600 hover:text-slate-800"
                      >
                        <span>{col.label}</span>
                        {col.sortKey ? (
                          <span
                            className={`text-[10px] leading-none ${
                              sortBy === col.sortKey
                                ? "text-slate-700"
                                : "text-slate-300"
                            }`}
                          >
                            {sortBy === col.sortKey
                              ? sortDir === "asc"
                                ? "^"
                                : "v"
                              : "-"}
                          </span>
                        ) : null}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={`${row.employee_id || "staff"}-${index}`}
                    className="border-t border-slate-100"
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3 text-slate-700">
                        {col.key === "ticket_count" ? (
                          <>
                            {row[col.key] ?? "--"}
                            {(() => {
                              const pctNode = renderPercent(
                                row?.mix?.tickets_pct ?? 0,
                                "tickets_pct",
                                {
                                  className:
                                    "cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2",
                                }
                              );
                              return pctNode ? (
                                <span className="ml-2 text-xs text-slate-500">
                                  ({pctNode})
                                </span>
                              ) : null;
                            })()}
                          </>
                        ) : col.key === "net_sales" ? (
                          <div>
                            <div className="font-medium text-slate-700">
                              {formatMoney(row[col.key])}
                              {(() => {
                                const pctNode = renderPercent(
                                  row?.mix?.net_pct ?? 0,
                                  "net_pct",
                                  {
                                    className:
                                      "cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2",
                                  }
                                );
                                return pctNode ? (
                                  <span className="ml-2 text-xs text-slate-500">
                                    ({pctNode})
                                  </span>
                                ) : null;
                              })()}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              Tip:{" "}
                              {renderPercent(
                                row?.mix?.tip_rate_pct ?? 0,
                                "tip_rate_pct",
                                {
                                  className:
                                    "cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2",
                                }
                              ) || "0.0%"}{" "}
                              - Refund:{" "}
                              {renderPercent(
                                row?.mix?.refund_rate_pct ?? 0,
                                "refund_rate_pct",
                                {
                                  className:
                                    "cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2",
                                }
                              ) || "0.0%"}
                              {groupBy === "employee_payment" ? (
                                <>
                                  {" "}
                                  - Pay mix (emp):{" "}
                                  {renderPercent(
                                    row?.mix?.payment_within_employee_pct ?? 0,
                                    "payment_within_employee_pct",
                                    {
                                      className:
                                        "cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2",
                                      context: { paymentType: row?.payment_type },
                                    }
                                  ) || "0.0%"}{" "}
                                  - Emp share (pay):{" "}
                                  {renderPercent(
                                    row?.mix?.employee_within_payment_pct ?? 0,
                                    "employee_within_payment_pct",
                                    {
                                      className:
                                        "cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2",
                                      context: { paymentType: row?.payment_type },
                                    }
                                  ) || "0.0%"}
                                </>
                              ) : null}
                            </div>
                          </div>
                        ) : col.key === "tips" ||
                          col.key === "tax" ||
                          col.key === "gross" ||
                          col.key === "refunds" ||
                          col.key === "cash" ||
                          col.key === "card" ||
                          col.key === "gift_card" ||
                          col.key === "other" ? (
                          formatMoney(row[col.key])
                        ) : (
                          row[col.key] ?? "--"
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-4 py-6 text-center text-slate-500"
                    >
                      {loading ? "Loading..." : "No results for this range."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm">
            <div className="text-slate-600">
              Page {meta.page} of {totalPages} - {meta.total_rows} rows
            </div>
            <div className="flex items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => {
                  const nextSize = Number(e.target.value);
                  setPageSize(nextSize);
                  setPage(1);
                  fetchReport(1, nextSize);
                }}
                className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
              >
                {[25, 50, 100, 200].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => fetchReport(Math.max(1, page - 1))}
                disabled={page <= 1 || loading}
                className="rounded-full border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => fetchReport(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages || loading}
                className="rounded-full border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
  );
}
